import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Typography,
  LinearProgress,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// API
import { getSegments, getProducts } from '../../../api/productSamplingWorkflowApi';

export default function ProductCatalog({ form, setForm }) {
  const isEditable = form.status_id === 1;
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState({ loading: false, items: [] });
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);

  const onAddProduct = (item) => {
    const exists = form?.products?.find((row) => row?.id === item.id);
    if (exists) return;
    setForm((prev) => ({
      ...prev,
      products: [
        ...(prev.products || []),
        {
          id: item.id,
          code: item.product_code,
          name: item.product_name,
          label: item.label,
          requested_qty: 0,
          confirmed_qty: 0,
        },
      ],
    }));
  };

  const loadProducts = async () => {
    setProducts({ ...products, loading: true });
    const request = {
      segment_id: selectedSegment,
    };
    const response = await getProducts(request);
    const { code, data } = response.data;

    if (code === '200') {
      setProducts({ ...products, loading: false, items: data });
    }
  };

  const loadSegments = async () => {
    const response = await getSegments();
    const { code, data } = response.data;

    if (code === '200') {
      setSegments(data);
    }
  };

  const filteredItems = products.items.filter((item) =>
    item.product_name.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    loadProducts();
  }, [selectedSegment]);

  useEffect(() => {
    loadSegments();
  }, []);

  return (
    <Paper sx={{ p: 2, height: 700 }}>
      <Typography fontWeight={700}>Product Catalog </Typography>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Click a product to add it to the plan
      </Typography>

      <OutlinedInput
        fullWidth
        size='small'
        placeholder='Search products...'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        startAdornment={
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        }
      />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 2,
          mb: 2,
        }}
      >
        <Chip
          key={0}
          label={'ALL'}
          clickable
          onClick={() => setSelectedSegment(null)}
          sx={{
            backgroundColor: selectedSegment === null ? '#2e7d32' : 'default',
            color: selectedSegment === null ? '#fff' : 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              backgroundColor: selectedSegment === null ? '#2e7d32' : 'rgba(0, 0, 0, 0.08)',
            },
            '&:active': {
              backgroundColor: selectedSegment === null ? '#2e7d32' : 'default',
            },
          }}
        />
        {segments.map((item, i) => (
          <Chip
            key={item?.segment_id}
            label={item?.segment_name}
            clickable
            onClick={() => setSelectedSegment(item?.segment_id)}
            sx={{
              backgroundColor: selectedSegment === item?.segment_id ? '#2e7d32' : 'default',
              color: selectedSegment === item?.segment_id ? '#fff' : 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                backgroundColor:
                  selectedSegment === item?.segment_id ? '#2e7d32' : 'rgba(0, 0, 0, 0.08)',
              },
              '&:active': {
                backgroundColor: selectedSegment === item?.segment_id ? '#2e7d32' : 'default',
              },
            }}
          />
        ))}
      </Box>

      <Box
        sx={{
          overflowY: 'auto',
          height: 450,
        }}
      >
        {products.loading ? (
          <LinearProgress />
        ) : (
          filteredItems.map((item) => {
            const selected = form?.products?.some((p) => p.id === item.id);

            return (
              <Box
                key={item.id}
                pl={1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out, background-color 0.2s',
                  '&:hover': {
                    backgroundColor: '#e4e1e1',
                  },
                  opacity: selected ? 0.5 : 1,
                }}
                onClick={() => (isEditable ? onAddProduct(item) : null)}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: '#f1f5f9',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 700,
                    mr: 2,
                  }}
                >
                  {item.product_name
                    .split(' ')
                    .map((name) => name.charAt(0))
                    .join('')
                    .toUpperCase()}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={500}>{item.product_name}</Typography>

                  <Typography variant='caption' color='text.secondary'>
                    {item.label}
                  </Typography>
                </Box>

                {isEditable ? (
                  selected ? (
                    <CheckCircleIcon color='success' fontSize='small' sx={{ marginRight: '8px' }} />
                  ) : (
                    <IconButton
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddProduct(item);
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  )
                ) : (
                  ''
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
}
